import { FileOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useRef, useState } from 'react'

import { notifyMethod } from '../../../App'
import useRedux from '../../../hooks/useRedux'
import useTranslations from '../../../hooks/useTranslations'
import { useFormInstanceFn } from '../../../shared/antd/ANTDForm'
import { getBase64 } from '../../../utils'
import { MAX_FILE_SIZE } from '../../../utils/constant'
import { pdfImage, videoFileImage, docImage } from '../../../utils/icons'
import { include, isEqual } from '../../../utils/javascript'
import { getImageFileApi } from '../../jobs/jobs.api'
import {
  FACING_MODE_ENVIRONMENT,
  FACING_MODE_USER,
} from '../common.description'

const formUpload = ({ name, hasApiCall }) => {
  const form = useFormInstanceFn()
  const { t } = useTranslations()

  const { selector } = useRedux()
  const isDesktop = selector(state => state.app.isDesktop)
  const [imagePreview, setImagePreview] = useState({
    visible: false,
    image: '',
    type: '',
  })
  const [takePhoto, setTakePhoto] = useState({
    flag: false,
    valid: '',
  })

  const [facingMode, setFacingMode] = useState(FACING_MODE_USER)
  const [showFlipBtn, setShowFlipBtn] = useState(false)
  const videoElement = useRef(null)
  const videoConstraints = {
    facingMode: FACING_MODE_USER,
  }

  useEffect(() => {
    const userAgent = navigator.userAgent?.toLowerCase()

    const isDesktopUA =
      /windows|macintosh|linux/.test(userAgent) &&
      !/mobile/.test(userAgent) &&
      !/tablet/.test(userAgent)

    const screenWidth = window.screen?.width

    if (!(isDesktopUA && screenWidth >= 1024)) {
      setFacingMode(FACING_MODE_ENVIRONMENT)
      setShowFlipBtn(true)
    }
  }, [])

  const handleTakePhoto = () => {
    setTakePhoto(prev => ({
      ...prev,
      flag: !prev.flag,
    }))
  }

  const handlePreview = async file => {
    let viewType = file?.type
    const fileName = file?.name || ''
    const isDoc =
      fileName.toLowerCase().endsWith('.doc') ||
      fileName.toLowerCase().endsWith('.docx')
    const isPdf =
      file?.type === 'application/pdf' ||
      fileName.toLowerCase().endsWith('.pdf')

    let url
    if (file?.originFileObj) {
      url = await getBase64(file?.originFileObj)
    } else if (file?.url) {
      url = file.url
    }
    url &&
      setImagePreview({
        visible: true,
        image: url,
        type: viewType || (isDoc ? 'doc' : isPdf ? 'pdf' : ''),
        fileName: fileName,
      })
  }

  const handleCancelPreview = () => {
    setImagePreview({ visible: false, image: '', type: '' })
  }

  const handleSwitchCam = useCallback(() => {
    setFacingMode(prevState =>
      isEqual(prevState, FACING_MODE_USER)
        ? FACING_MODE_ENVIRONMENT
        : FACING_MODE_USER,
    )
  }, [])

  const handleError = err => {
    setTakePhoto(prev => ({
      ...prev,
      valid: t('msg_YourCameraIsNotConnected'),
    }))
  }

  const dataURLtoFile = dataUrl => {
    let arr = dataUrl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], `${Date.now()}.png`, { type: mime })
  }

  const onFileUpload = async ({ file }) => {
    const isLt5MB = file?.size < MAX_FILE_SIZE
    if (!isLt5MB) {
      return notifyMethod.warning({
        message: t('msg_MaximumSizeAllowed', {
          maxSize: MAX_FILE_SIZE / 1024 / 1024,
        }),
      })
    }
    const formData = new FormData()
    formData.append('file', file)
    const resp = await getImageFileApi({
      params: '?source=USER',
      payload: formData,
    })
    return resp?.data?.dmsId
  }

  const handleCapture = async () => {
    const imageSrc = videoElement.current.getScreenshot()
    let file = dataURLtoFile(imageSrc)
    const fileList = form.getFieldValue(name)?.fileList

    setTakePhoto(prev => ({ ...prev, flag: false }))
    if (!hasApiCall) {
      form.setFieldValue(name, {
        fileList: [...(fileList || []), { ...file, originFileObj: file }],
      })
    } else {
      const dmsId = await onFileUpload({ file })
      if (dmsId) {
        form.setFieldValue(name, {
          fileList: [
            ...(fileList || []),
            { ...file, dmsId, uid: dmsId, originFileObj: file },
          ],
        })
      }
    }
  }

  const handleIconRender = file => {
    const fileType = file?.type || /\.\w+$/.exec(file?.url)?.[0]
    const videoType = [
      '.mp4',
      '.mov',
      '.avi',
      '.wmv',
      '.avchd',
      '.webm',
      '.flv',
    ]
    const docType =
      file?.name?.toLowerCase().endsWith('.doc') ||
      file?.name?.toLowerCase().endsWith('.docx')
    if (include(fileType, 'pdf')) {
      return <img src={pdfImage} alt="pdfIcon" />
    } else if (docType) {
      return <img src={docImage} alt="docIcon" />
    } else if (include(fileType, 'spreadsheetml.sheet')) {
      return <FileOutlined />
    } else if (videoType.some(v => include(fileType, v))) {
      return <img src={videoFileImage} alt="videoIcon" />
    } else if (['.csv', '.xlsx'].some(v => include(fileType, v))) {
      return <img src={pdfImage} alt="videoIcon" />
    } else {
      return <img src={file?.url} alt="pic" />
    }
  }

  return {
    takePhoto,
    isDesktop,
    facingMode,
    showFlipBtn,
    imagePreview,
    videoElement,
    videoConstraints,
    handleError,
    handleCapture,
    handlePreview,
    handleSwitchCam,
    handleTakePhoto,
    handleCancelPreview,
    handleIconRender,
  }
}

export default formUpload
